import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Tv, Play, CheckCircle, Clock, ExternalLink, Bell, MessageSquare, Send, Reply, Trash2, AlertTriangle, ChevronDown, ChevronUp, Image, Upload, Users, Shield, Heart, Ban, ChevronLeft, ChevronRight, Smile } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import EmojiPicker from 'emoji-picker-react';

// Always use tradepayflow.emergent.host for bonbot.biz custom domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

// Get base URL for static files (uploads)
const getBaseUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz';
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

// Helper to get full image URL - call getBaseUrl() at runtime instead of using cached value
const getFullImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  // If already absolute URL, return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  // Otherwise prepend base URL - call getBaseUrl() to get fresh value
  const baseUrl = getBaseUrl();
  let full = `${baseUrl}${imageUrl}`;
  // New Emergent-Storage-backed URLs live at /api/chat-images/{id} and need ?auth=<token>
  // because <img src> cannot pass Authorization headers.
  if (imageUrl.startsWith('/api/chat-images/')) {
    const token = localStorage.getItem('guest_token') || localStorage.getItem('admin_token') || localStorage.getItem('support_token') || '';
    if (token) {
      full += (imageUrl.includes('?') ? '&' : '?') + 'auth=' + encodeURIComponent(token);
    }
  }
  return full;
};

// Helper to extract video ID and create embed URL
const getEmbedUrl = (url) => {
  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }
  
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  
  return null;
};

// Helper to get thumbnail
const getThumbnail = (url) => {
  const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (youtubeMatch) {
    return `https://img.youtube.com/vi/${youtubeMatch[1]}/mqdefault.jpg`;
  }
  return null;
};

// Check if message contains sensitive info (email or phone)
const containsSensitiveInfo = (text) => {
  // Email pattern
  const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi;
  
  // Phone patterns (various formats)
  const phonePatterns = [
    /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g,
    /\(\d{3}\)\s*\d{3}[-.]?\d{4}/g,
    /\b\d{3}\s+\d{3}\s+\d{4}\b/g,
    /\+\d{1,3}[-.\s]?\d{3,4}[-.\s]?\d{3,4}[-.\s]?\d{3,4}/g,
    /\b\d{10,12}\b/g,
  ];
  
  if (emailPattern.test(text)) {
    return { found: true, type: 'email' };
  }
  
  for (const pattern of phonePatterns) {
    if (pattern.test(text)) {
      return { found: true, type: 'phone' };
    }
  }
  
  return { found: false, type: null };
};

// Community Guidelines Modal
const CommunityGuidelinesModal = ({ isOpen, onAccept }) => {
  if (!isOpen) return null;
  
  return (
    <div className="absolute inset-0 bg-black/90 backdrop-blur-sm z-10 flex items-center justify-center p-2">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-white/20 max-w-sm w-full p-4 shadow-2xl">
        <div className="text-center mb-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-2">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-lg font-bold text-white">Community Guidelines</h3>
        </div>
        
        <div className="space-y-1.5 mb-3">
          <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
            <Heart className="w-4 h-4 text-pink-400 flex-shrink-0" />
            <p className="text-white text-xs">Be respectful to others</p>
          </div>
          
          <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
            <Shield className="w-4 h-4 text-blue-400 flex-shrink-0" />
            <p className="text-white text-xs">No phone numbers or emails</p>
          </div>
          
          <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
            <Users className="w-4 h-4 text-green-400 flex-shrink-0" />
            <p className="text-white text-xs">Stay on topic - trading only</p>
          </div>
          
          <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <p className="text-white text-xs">No spam or promotions</p>
          </div>
          
          <div className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <p className="text-red-300 text-xs font-medium">No financial advice - educational only</p>
          </div>
        </div>
        
        <p className="text-gray-500 text-[10px] text-center mb-3">
          Violations may result in suspension from chat
        </p>
        
        <Button
          onClick={onAccept}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 h-9 text-sm"
          data-testid="accept-guidelines-btn"
        >
          I Agree - Join Chat
        </Button>
      </div>
    </div>
  );
};

// Image Preview Modal
const ImagePreviewModal = ({ imageUrl, onClose }) => {
  if (!imageUrl) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh]">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300"
        >
          <X className="w-6 h-6" />
        </button>
        <img 
          src={imageUrl} 
          alt="Preview" 
          className="max-w-full max-h-[85vh] rounded-lg object-contain"
        />
      </div>
    </div>
  );
};

// Comment Component
const Comment = ({ comment, onReply, onDelete, onSuspend, isAdmin, currentUserId, userToken, level = 0 }) => {
  const [showReplies, setShowReplies] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  const handleSubmitReply = async () => {
    if (!replyText.trim()) return;
    
    const sensitiveCheck = containsSensitiveInfo(replyText);
    if (sensitiveCheck.found) {
      toast.error(
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold">Message Blocked!</p>
            <p className="text-sm">Sharing {sensitiveCheck.type === 'email' ? 'email addresses' : 'phone numbers'} is not allowed.</p>
          </div>
        </div>,
        { duration: 5000 }
      );
      return;
    }
    
    setSubmittingReply(true);
    try {
      await onReply(comment.id, replyText);
      setReplyText('');
      setShowReplyInput(false);
    } finally {
      setSubmittingReply(false);
    }
  };
  
  const handleSuspendUser = async () => {
    if (!comment.user_id) return;
    if (!window.confirm(`Suspend ${comment.user_name} from chat for community guideline violation?`)) return;
    
    try {
      const response = await fetch(`${API}/education/suspend-user/${encodeURIComponent(comment.user_id)}?token=${userToken}&reason=Community guideline violation`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`${comment.user_name} has been suspended from chat`);
        if (onSuspend) onSuspend(comment.user_id);
      } else {
        toast.error(data.message || 'Failed to suspend user');
      }
    } catch (err) {
      toast.error('Failed to suspend user');
    }
  };
  
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };
  
  return (
    <div className={`${level > 0 ? 'ml-6 border-l-2 border-white/10 pl-3' : ''}`}>
      <div className="bg-white/5 rounded-lg p-3 mb-2 hover:bg-white/[0.07] transition-colors">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm font-bold">
                {comment.user_name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div>
              <span className="text-white font-medium text-sm">{comment.user_name}</span>
              <span className="text-gray-500 text-xs ml-2">{formatTime(comment.created_at)}</span>
            </div>
          </div>
          
          {/* Admin Actions */}
          {isAdmin && (
            <div className="flex items-center gap-1">
              <button
                onClick={handleSuspendUser}
                className="p-1 rounded hover:bg-orange-500/20 text-gray-500 hover:text-orange-400 transition-colors"
                title="Suspend user from chat"
              >
                <Ban className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(comment.id)}
                className="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 transition-colors"
                title="Delete comment"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        
        <p className="text-gray-300 text-sm mt-2 ml-10 break-words">{comment.text}</p>
        
        {/* Image attachment */}
        {comment.image_url && (
          <div className="mt-2 ml-10">
            <img 
              src={getFullImageUrl(comment.image_url)} 
              alt="Attachment" 
              className="max-w-[200px] max-h-[150px] rounded-lg cursor-pointer hover:opacity-80 transition-opacity object-cover"
              onClick={() => setPreviewImage(getFullImageUrl(comment.image_url))}
            />
          </div>
        )}
        
        <div className="flex items-center gap-3 mt-2 ml-10">
          <button
            onClick={() => setShowReplyInput(!showReplyInput)}
            className="text-xs text-gray-500 hover:text-purple-400 flex items-center gap-1 transition-colors"
          >
            <Reply className="w-3 h-3" />
            Reply
          </button>
        </div>
        
        {showReplyInput && (
          <div className="mt-3 ml-10 flex gap-2">
            <Input
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={`Reply to ${comment.user_name}...`}
              className="flex-1 bg-white/5 border-white/10 text-white text-sm h-8"
              onKeyDown={(e) => e.key === 'Enter' && handleSubmitReply()}
            />
            <Button
              size="sm"
              onClick={handleSubmitReply}
              disabled={!replyText.trim() || submittingReply}
              className="bg-purple-600 hover:bg-purple-700 h-8"
            >
              <Send className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
      
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-1">
          <button
            onClick={() => setShowReplies(!showReplies)}
            className="text-xs text-purple-400 flex items-center gap-1 mb-2 ml-2"
          >
            {showReplies ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
          </button>
          
          {showReplies && comment.replies.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              onReply={onReply}
              onDelete={onDelete}
              onSuspend={onSuspend}
              isAdmin={isAdmin}
              currentUserId={currentUserId}
              userToken={userToken}
              level={level + 1}
            />
          ))}
        </div>
      )}
      
      {/* Image Preview Modal */}
      <ImagePreviewModal imageUrl={previewImage} onClose={() => setPreviewImage(null)} />
    </div>
  );
};

// Live Chat Room Component
const LiveChatRoom = ({ userToken, userName, isAdmin = false, contentId = 'community-chat' }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [guidelinesAccepted, setGuidelinesAccepted] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const commentsEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastCommentCountRef = useRef(0);
  const emojiPickerRef = useRef(null);
  
  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle emoji selection
  const onEmojiClick = (emojiData) => {
    setNewComment(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };
  
  // Check if guidelines already accepted
  useEffect(() => {
    const accepted = localStorage.getItem('chat_guidelines_accepted');
    if (accepted) {
      setGuidelinesAccepted(true);
    } else {
      setShowGuidelines(true);
    }
  }, []);
  
  // Fetch comments
  const fetchComments = useCallback(async () => {
    try {
      const response = await fetch(`${API}/education/${contentId}/comments?token=${userToken}`);
      const data = await response.json();
      if (data.success) {
        const newComments = data.comments || [];
        
        // Check for new comments and show notification
        if (newComments.length > lastCommentCountRef.current && lastCommentCountRef.current > 0) {
          const newCount = newComments.length - lastCommentCountRef.current;
          toast.info(`${newCount} new message${newCount > 1 ? 's' : ''} in chat`, { duration: 2000 });
        }
        lastCommentCountRef.current = newComments.length;
        
        setComments(newComments);
        setOnlineCount(data.online_count || Math.floor(Math.random() * 5) + 1);
      }
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoading(false);
    }
  }, [contentId, userToken]);
  
  useEffect(() => {
    if (guidelinesAccepted) {
      fetchComments();
      pollIntervalRef.current = setInterval(fetchComments, 5000); // Poll every 5 seconds for live feel
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [guidelinesAccepted, fetchComments]);
  
  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [comments]);
  
  // Handle guidelines accept
  const handleAcceptGuidelines = () => {
    localStorage.setItem('chat_guidelines_accepted', 'true');
    setGuidelinesAccepted(true);
    setShowGuidelines(false);
  };
  
  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    
    setSelectedImage(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };
  
  // Clear selected image
  const clearSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Submit new comment
  const handleSubmitComment = async () => {
    if (!newComment.trim() && !selectedImage) return;
    
    // Check for sensitive info in text
    if (newComment.trim()) {
      const sensitiveCheck = containsSensitiveInfo(newComment);
      if (sensitiveCheck.found) {
        toast.error(
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Message Blocked!</p>
              <p className="text-sm">Sharing {sensitiveCheck.type === 'email' ? 'email addresses' : 'phone numbers'} is not allowed.</p>
            </div>
          </div>,
          { duration: 5000 }
        );
        return;
      }
    }
    
    setSubmitting(true);
    try {
      let imageUrl = null;
      
      // Upload image first if selected
      if (selectedImage) {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('file', selectedImage);
        
        const uploadResponse = await fetch(`${API}/education/upload-image?token=${userToken}`, {
          method: 'POST',
          body: formData
        });
        
        const uploadData = await uploadResponse.json();
        if (uploadData.success) {
          imageUrl = uploadData.image_url;
        } else {
          toast.error(uploadData.message || 'Failed to upload image');
          setUploadingImage(false);
          setSubmitting(false);
          return;
        }
        setUploadingImage(false);
      }
      
      const response = await fetch(`${API}/education/${contentId}/comments?token=${userToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text: newComment.trim() || '📷 Shared an image',
          image_url: imageUrl
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setNewComment('');
        clearSelectedImage();
        fetchComments();
      } else if (data.suspended) {
        toast.error(
          <div className="flex items-start gap-2">
            <Ban className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Chat Access Suspended</p>
              <p className="text-sm">{data.message}</p>
            </div>
          </div>,
          { duration: 5000 }
        );
      } else if (data.blocked) {
        toast.error(
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Message Blocked!</p>
              <p className="text-sm">{data.message}</p>
            </div>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.error(data.message || 'Failed to post message');
      }
    } catch (err) {
      toast.error('Failed to post message');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Reply to comment
  const handleReply = async (parentId, text) => {
    try {
      const response = await fetch(`${API}/education/${contentId}/comments/${parentId}/reply?token=${userToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text })
      });
      
      const data = await response.json();
      if (data.success) {
        fetchComments();
        toast.success('Reply posted!');
      } else if (data.suspended) {
        toast.error(
          <div className="flex items-start gap-2">
            <Ban className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Chat Access Suspended</p>
              <p className="text-sm">{data.message}</p>
            </div>
          </div>,
          { duration: 5000 }
        );
      } else if (data.blocked) {
        toast.error(data.message);
      } else {
        toast.error(data.message || 'Failed to post reply');
      }
    } catch (err) {
      toast.error('Failed to post reply');
    }
  };
  
  // Delete comment (admin only)
  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      const response = await fetch(`${API}/education/comments/${commentId}?token=${userToken}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      if (data.success) {
        fetchComments();
        toast.success('Message deleted');
      } else {
        toast.error(data.message || 'Failed to delete');
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };
  
  return (
    <div className="relative h-full flex flex-col">
      {/* Community Guidelines Modal */}
      <CommunityGuidelinesModal 
        isOpen={showGuidelines} 
        onAccept={handleAcceptGuidelines}
      />
      
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-purple-400" />
          <h4 className="text-white font-semibold">Live Chat</h4>
          <Badge className="bg-green-500/20 text-green-400 text-xs">
            <span className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse" />
            {onlineCount} online
          </Badge>
        </div>
        <span className="text-gray-500 text-xs">{comments.length} messages</span>
      </div>
      
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-600" />
            <p className="text-gray-400">No messages yet</p>
            <p className="text-gray-500 text-sm">Be the first to say hello!</p>
          </div>
        ) : (
          comments.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
              onReply={handleReply}
              onDelete={handleDelete}
              onSuspend={() => fetchComments()}
              isAdmin={isAdmin}
              currentUserId={null}
              userToken={userToken}
            />
          ))
        )}
        <div ref={commentsEndRef} />
      </div>
      
      {/* Image Preview */}
      {imagePreview && (
        <div className="px-4 py-2 border-t border-white/10 bg-white/5">
          <div className="relative inline-block">
            <img 
              src={imagePreview} 
              alt="Preview" 
              className="max-h-20 rounded-lg"
            />
            <button
              onClick={clearSelectedImage}
              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        </div>
      )}
      
      {/* Input Area */}
      <div className="p-3 border-t border-white/10 flex-shrink-0">
        <div className="flex gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-bold">
              {userName?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 flex gap-2 relative">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageSelect}
              accept="image/*"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              title="Upload screenshot (5MB max)"
              disabled={uploadingImage}
            >
              <Image className="w-5 h-5" />
            </button>
            
            {/* Emoji Picker Button */}
            <div className="relative" ref={emojiPickerRef}>
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-yellow-400 transition-colors"
                title="Add emoji"
              >
                <Smile className="w-5 h-5" />
              </button>
              
              {/* Emoji Picker Dropdown */}
              {showEmojiPicker && (
                <div className="absolute bottom-full mb-2 left-0 z-50">
                  <EmojiPicker
                    onEmojiClick={onEmojiClick}
                    theme="dark"
                    width={300}
                    height={350}
                    searchDisabled={false}
                    skinTonesDisabled={true}
                    previewConfig={{ showPreview: false }}
                  />
                </div>
              )}
            </div>
            
            <Input
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 bg-white/5 border-white/10 text-white"
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmitComment()}
              disabled={submitting}
              data-testid="chat-message-input"
            />
            <Button
              onClick={handleSubmitComment}
              disabled={(!newComment.trim() && !selectedImage) || submitting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="chat-send-btn"
            >
              {submitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
        <p className="text-gray-500 text-xs mt-2 text-center">
          📷 Max 5MB • No phone/email sharing
        </p>
      </div>
    </div>
  );
};

// Comments Section Component (for videos)
const CommentsSection = ({ contentId, userToken, userName, isAdmin = false }) => {
  return (
    <div className="border-t border-white/10">
      <LiveChatRoom 
        userToken={userToken}
        userName={userName}
        isAdmin={isAdmin}
        contentId={contentId}
      />
    </div>
  );
};

const EducationTVModal = ({ isOpen, onClose, userToken, userName, isAdmin = false }) => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeTab, setActiveTab] = useState('chat'); // Default to 'chat' for community focus

  useEffect(() => {
    if (isOpen && userToken) {
      fetchContent();
    }
  }, [isOpen, userToken]);

  // Auto-switch to videos tab if there are videos available
  useEffect(() => {
    if (!loading && content.length > 0) {
      // Keep user on their current tab, don't force switch
    }
  }, [loading, content]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/education?token=${userToken}`);
      const data = await response.json();
      if (data.success) {
        setContent(data.content || []);
      }
    } catch (err) {
      toast.error('Failed to load education content');
    } finally {
      setLoading(false);
    }
  };

  const markAsViewed = async (contentId) => {
    try {
      await fetch(`${API}/education/${contentId}/mark-viewed?token=${userToken}`, {
        method: 'POST'
      });
      setContent(prev => prev.map(c => 
        c.id === contentId ? { ...c, is_viewed: true } : c
      ));
    } catch (err) {
      // Ignore errors
    }
  };

  const handlePlayVideo = (item) => {
    setSelectedVideo(item);
    if (!item.is_viewed) {
      markAsViewed(item.id);
    }
  };

  // Navigate to previous video
  const goToPreviousVideo = () => {
    if (!selectedVideo || content.length === 0) return;
    const currentIndex = content.findIndex(v => v.id === selectedVideo.id);
    if (currentIndex > 0) {
      const prevVideo = content[currentIndex - 1];
      setSelectedVideo(prevVideo);
      if (!prevVideo.is_viewed) {
        markAsViewed(prevVideo.id);
      }
    }
  };

  // Navigate to next video
  const goToNextVideo = () => {
    if (!selectedVideo || content.length === 0) return;
    const currentIndex = content.findIndex(v => v.id === selectedVideo.id);
    if (currentIndex < content.length - 1) {
      const nextVideo = content[currentIndex + 1];
      setSelectedVideo(nextVideo);
      if (!nextVideo.is_viewed) {
        markAsViewed(nextVideo.id);
      }
    }
  };

  // Get current video index
  const getCurrentVideoIndex = () => {
    if (!selectedVideo || content.length === 0) return -1;
    return content.findIndex(v => v.id === selectedVideo.id);
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
  };

  if (!isOpen) return null;

  // Video player view with comments
  if (selectedVideo) {
    const embedUrl = getEmbedUrl(selectedVideo.video_url);
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <div className="relative w-full max-w-4xl bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl overflow-hidden border border-white/10 max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
            <div>
              <h3 className="text-xl font-bold text-white">{selectedVideo.title}</h3>
              <p className="text-sm text-gray-400">{selectedVideo.description}</p>
            </div>
            <button 
              onClick={() => setSelectedVideo(null)}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            {/* Video Player */}
            <div className="aspect-video bg-black">
              {embedUrl ? (
                <iframe
                  src={embedUrl}
                  title={selectedVideo.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-white mb-4">Unable to embed video</p>
                    <a
                      href={selectedVideo.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open in new tab
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Comments Section */}
            <CommentsSection
              contentId={selectedVideo.id}
              userToken={userToken}
              userName={userName}
              isAdmin={isAdmin}
            />
          </div>

          {/* Navigation Footer */}
          <div className="p-4 border-t border-white/10 flex-shrink-0">
            <div className="flex items-center justify-between">
              {/* Back Button */}
              <Button
                variant="ghost"
                onClick={() => setSelectedVideo(null)}
                className="text-gray-400 hover:text-white"
              >
                ← Back to all videos
              </Button>

              {/* Video Counter */}
              {content.length > 1 && (
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <span>{getCurrentVideoIndex() + 1} of {content.length}</span>
                </div>
              )}

              {/* Navigation Arrows */}
              {content.length > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToPreviousVideo}
                    disabled={getCurrentVideoIndex() === 0}
                    className="border-white/20 text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-30"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToNextVideo}
                    disabled={getCurrentVideoIndex() === content.length - 1}
                    className="border-white/20 text-gray-300 hover:text-white hover:bg-white/10 disabled:opacity-30"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Content list view with tabs
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl overflow-hidden border border-white/10 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-5 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Tv className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Tool & Education TV</h2>
                <p className="text-white/80 text-sm">Learn & Chat with the community</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 flex-shrink-0">
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'chat' 
                ? 'text-purple-400 border-b-2 border-purple-400 bg-white/5' 
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="community-chat-tab"
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Community Chat
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'videos' 
                ? 'text-purple-400 border-b-2 border-purple-400 bg-white/5' 
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="videos-tab"
          >
            <Tv className="w-4 h-4 inline mr-2" />
            Videos ({content.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'chat' ? (
            <div className="h-full" style={{ height: '500px' }}>
              <LiveChatRoom 
                userToken={userToken}
                userName={userName}
                isAdmin={isAdmin}
                contentId="community-chat"
              />
            </div>
          ) : (
            <div className="p-6 overflow-y-auto h-full">
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-gray-400">Loading content...</p>
                </div>
              ) : content.length === 0 ? (
                <div className="text-center py-12">
                  <Tv className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400 text-lg">No videos available</p>
                  <p className="text-gray-500 text-sm mt-2">Check back later for new content</p>
                  <Button
                    onClick={() => setActiveTab('chat')}
                    className="mt-4 bg-purple-600 hover:bg-purple-700"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Join Community Chat
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {content.map((item) => {
                    const thumbnail = getThumbnail(item.video_url);
                    
                    return (
                      <div
                        key={item.id}
                        onClick={() => handlePlayVideo(item)}
                        className={`flex gap-4 p-4 rounded-xl cursor-pointer transition-all ${
                          item.is_viewed 
                            ? 'bg-white/5 border border-white/10' 
                            : 'bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20'
                        }`}
                      >
                        {/* Thumbnail */}
                        <div className="relative w-32 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-black">
                          {thumbnail ? (
                            <img src={thumbnail} alt={item.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900 to-pink-900">
                              <Tv className="w-8 h-8 text-white/50" />
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors">
                            <Play className="w-8 h-8 text-white" />
                          </div>
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-white truncate">{item.title}</h3>
                            {item.is_viewed ? (
                              <Badge className="bg-green-500/20 text-green-400 flex-shrink-0">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Viewed
                              </Badge>
                            ) : (
                              <Badge className="bg-purple-500/20 text-purple-300 flex-shrink-0">
                                New
                              </Badge>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-sm text-gray-400 mt-1 line-clamp-2">{item.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {getTimeRemaining(item.expires_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              {item.comment_count || 0} comments
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Floating TV Button component
export const EducationTVButton = ({ onClick, notificationCount = 0 }) => {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-6 right-6 z-40 group"
    >
      <div className="relative w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center">
        <Tv className="w-6 h-6 text-white" />
        
        {/* Notification badge */}
        {notificationCount > 0 && (
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
            <span className="text-xs font-bold text-white">{notificationCount > 9 ? '9+' : notificationCount}</span>
          </div>
        )}
        
        {/* Tooltip */}
        <div className="absolute right-full mr-3 px-3 py-1.5 bg-slate-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Tool & Education TV
          {notificationCount > 0 && (
            <span className="ml-2 text-purple-300">({notificationCount} new)</span>
          )}
        </div>
      </div>
    </button>
  );
};

// Notification Bell component for header
export const EducationNotificationBell = ({ count = 0, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
    >
      <Bell className={`w-5 h-5 ${count > 0 ? 'text-yellow-400' : 'text-gray-400'}`} />
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
          <span className="text-[10px] font-bold text-white">{count > 9 ? '9+' : count}</span>
        </span>
      )}
    </button>
  );
};

export default EducationTVModal;
